/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// ============================================================================
// USE ESTIMATE LINE ITEMS HOOK - Line Item Management (React Query)
// ============================================================================

import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { useOrganizationQueryGuard } from "@/hooks/use-organization-query";
import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import { calculateLineItemTotalsSummary } from "@/lib/business-logic/estimates";
import { estimateQueryKeys } from "@/lib/query/query-keys";
import { logger } from "@/lib/utils/logger";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "sonner";
// ============================================================================
// TYPES
// ============================================================================

export interface EstimateLineItem {
  line_item_id: string; // Updated from estimate_line_item_id to match unified table
  estimate_id: string;
  description: string;
  quantity: number;
  unit: string;
  unit_cost: number;
  total_cost: number;
  material_cost: number; // Extracted from calculated_fields JSONB
  labor_cost: number; // Extracted from calculated_fields JSONB
  equipment_cost: number; // Extracted from calculated_fields JSONB
  notes?: string | null;
  category_id?: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  // Keep estimate_line_item_id for backward compatibility (maps to line_item_id)
  estimate_line_item_id?: string;
}

export interface CreateLineItemData {
  description: string;
  quantity: number;
  unit: string;
  unit_cost: number;
  material_cost?: number;
  labor_cost?: number;
  equipment_cost?: number;
  notes?: string;
  category_id?: string;
}

export interface UpdateLineItemData extends Partial<CreateLineItemData> {
  line_item_id: string; // Updated from estimate_line_item_id
  estimate_line_item_id?: string; // Keep for backward compatibility
  sort_order?: number;
}

// ============================================================================
// HOOK
// ============================================================================

export function useEstimateLineItems(estimateId: string) {
  const { getToken } = useAuthSafe();
  const queryClient = useQueryClient();
  const { organizationId, queryEnabled: organizationReady } =
    useOrganizationQueryGuard({
      enabled: Boolean(estimateId),
      debugKey: "useEstimateLineItems",
    });
  const ensureContextReady = () => {
    if (!organizationReady) {
      throw new Error(
        "Still loading your organization. Give it a second and try again.",
      );
    }
  };

  const api = useMemo(
    () =>
      createApiClient({
        getToken,
        getOrganizationId: () => organizationId ?? undefined,
        autoRetry429: true,
      }),
    [getToken, organizationId],
  );

  const queryKey = estimateQueryKeys.lineItems(estimateId);

  // ============================================================================
  // QUERY - FETCH LINE ITEMS
  // ============================================================================

  const {
    data: lineItems = [],
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await api.get<{ lineItems?: EstimateLineItem[] }>(
        `/api/estimates/${estimateId}/line-items`,
      );
      if (isApiFailure(res)) {
        throw new Error(res.message || "Failed to fetch line items");
      }
      return res.data?.lineItems ?? [];
    },
    enabled: organizationReady && Boolean(estimateId),
  });

  const error = queryError?.message ?? null;

  // ============================================================================
  // MUTATION - CREATE LINE ITEM
  // ============================================================================

  const createMutation = useMutation({
    mutationFn: async (data: CreateLineItemData) => {
      ensureContextReady();
      const res = await api.post<EstimateLineItem>(
        `/api/estimates/${estimateId}/line-items`,
        data,
      );
      if (isApiFailure(res)) {
        throw new Error(res.message || "Failed to create line item");
      }
      return res.data;
    },
    onMutate: async (newItem) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<EstimateLineItem[]>(queryKey);

      const optimistic: EstimateLineItem = {
        line_item_id: `temp-${Date.now()}`,
        estimate_id: estimateId,
        description: newItem.description,
        quantity: newItem.quantity,
        unit: newItem.unit,
        unit_cost: newItem.unit_cost,
        total_cost: newItem.quantity * newItem.unit_cost,
        material_cost: newItem.material_cost ?? 0,
        labor_cost: newItem.labor_cost ?? 0,
        equipment_cost: newItem.equipment_cost ?? 0,
        notes: newItem.notes ?? null,
        category_id: newItem.category_id ?? null,
        sort_order: (previous?.length ?? 0) + 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      queryClient.setQueryData<EstimateLineItem[]>(queryKey, (old) => [
        ...(old ?? []),
        optimistic,
      ]);
      return { previous };
    },
    onError: (_err, _newItem, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
      toast.error("Failed to create line item");
    },
    onSuccess: () => {
      toast.success("Line item created");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // ============================================================================
  // MUTATION - UPDATE LINE ITEM
  // ============================================================================

  const updateMutation = useMutation({
    mutationFn: async (data: UpdateLineItemData) => {
      ensureContextReady();
      const lineItemId = data.line_item_id || data.estimate_line_item_id;
      if (!lineItemId) {
        throw new Error("Line item ID is required");
      }
      const res = await api.put<EstimateLineItem>(
        `/api/estimates/${estimateId}/line-items/${lineItemId}`,
        data,
      );
      if (isApiFailure(res)) {
        throw new Error(res.message || "Failed to update line item");
      }
      return {
        ...res.data,
        estimate_line_item_id: res.data.line_item_id,
      };
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<EstimateLineItem[]>(queryKey);
      const lineItemId = data.line_item_id || data.estimate_line_item_id;

      queryClient.setQueryData<EstimateLineItem[]>(queryKey, (old) =>
        (old ?? []).map((item) =>
          item.line_item_id === lineItemId
            ? {
                ...item,
                ...data,
                total_cost:
                  (data.quantity ?? item.quantity) *
                  (data.unit_cost ?? item.unit_cost),
                updated_at: new Date().toISOString(),
              }
            : item,
        ),
      );
      return { previous };
    },
    onError: (_err, _data, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
      toast.error("Failed to update line item");
    },
    onSuccess: () => {
      toast.success("Line item updated");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // ============================================================================
  // MUTATION - DELETE LINE ITEM
  // ============================================================================

  const deleteMutation = useMutation({
    mutationFn: async (lineItemId: string) => {
      ensureContextReady();
      const res = await api.del(
        `/api/estimates/${estimateId}/line-items/${lineItemId}`,
      );
      if (isApiFailure(res)) {
        throw new Error(res.message || "Failed to delete line item");
      }
    },
    onMutate: async (lineItemId) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<EstimateLineItem[]>(queryKey);

      queryClient.setQueryData<EstimateLineItem[]>(queryKey, (old) =>
        (old ?? []).filter((item) => item.line_item_id !== lineItemId),
      );
      return { previous };
    },
    onError: (_err, _lineItemId, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
      toast.error("Failed to delete line item");
    },
    onSuccess: () => {
      toast.success("Line item deleted");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // ============================================================================
  // REORDER LINE ITEMS
  // ============================================================================

  const reorderLineItems = async (lineItemIds: string[]): Promise<boolean> => {
    if (!estimateId) return false;

    try {
      // Update sort order for each line item
      const updatePromises = lineItemIds.map((lineItemId, index) =>
        api.put(`/api/estimates/${estimateId}/line-items/${lineItemId}`, {
          line_item_id: lineItemId,
          sort_order: index + 1,
        }),
      );

      const results = await Promise.allSettled(updatePromises);
      const failures = results.filter((r) => r.status === "rejected");

      // Always refetch to get actual server state
      await queryClient.invalidateQueries({ queryKey });

      if (failures.length > 0) {
        logger.error("Partial reorder failure", {
          estimateId,
          lineItemIds,
          failedCount: failures.length,
          totalCount: results.length,
        });
        toast.error("Error");
        return false;
      }

      toast.success("Success", {
        description: "Line items reordered successfully",
      });
      return true;
    } catch (err) {
      logger.error("Failed to reorder line items", {
        error: err,
        estimateId,
        lineItemIds,
      });
      toast.error("Error");
      return false;
    }
  };

  // ============================================================================
  // CALCULATE TOTALS
  // ============================================================================

  const calculateTotals = () => calculateLineItemTotalsSummary(lineItems);

  // ============================================================================
  // RESET STATE
  // ============================================================================

  const reset = () => {
    queryClient.setQueryData(queryKey, []);
  };

  // ============================================================================
  // WRAPPER FUNCTIONS (maintain existing API contract)
  // ============================================================================

  const createLineItem = async (
    data: CreateLineItemData,
  ): Promise<EstimateLineItem | null> => {
    try {
      return await createMutation.mutateAsync(data);
    } catch {
      return null;
    }
  };

  const updateLineItem = async (
    data: UpdateLineItemData,
  ): Promise<EstimateLineItem | null> => {
    try {
      return await updateMutation.mutateAsync(data);
    } catch {
      return null;
    }
  };

  const deleteLineItem = async (lineItemId: string): Promise<boolean> => {
    try {
      await deleteMutation.mutateAsync(lineItemId);
      return true;
    } catch {
      return false;
    }
  };

  return {
    // State
    lineItems,
    loading,
    error,

    // Actions
    fetchLineItems: refetch,
    createLineItem,
    updateLineItem,
    deleteLineItem,
    reorderLineItems,
    reset,

    // Computed
    calculateTotals,

    // Utilities
    hasLineItems: lineItems.length > 0,

    // Mutation states (additional for React Query)
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
